import type { AssessmentMedia } from '../../model/assessments/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface AssessmentTemplateUploadMediaApiOptions {
  templateId: string | number
  file: File
  /** 0–100. Only fires while the browser reports a computable total. */
  onProgress?: (percent: number) => void
  onStart?: () => void
  onSuccess?: (media: AssessmentMedia) => void
  onError?: (message: string) => void
  onFinish?: () => void
}

/**
 * `POST /admin-api/assessment-templates/{template_id}/media` — image or audio upload (contract §7).
 *
 * `multipart/form-data` with a single `file`; the `201` answers `{ path, url, expires_at }`, and
 * `path` is the value a document stores. Two deliberate departures from the other template hooks:
 *  - no `commonStore.setLoading` — the global loader would cover the editor for the length of an
 *    upload, so the caller owns progress via `onProgress` / `onStart` / `onFinish`.
 *  - no `Content-Type` header — axios must set the multipart boundary itself.
 */
export const useAssessmentTemplateUploadMediaApi = async (
  options: AssessmentTemplateUploadMediaApiOptions,
): Promise<AssessmentMedia | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()

  const url = useAdminRoutes('admin.assessment-templates.media.store', { template_id: options.templateId })

  options.onStart?.()

  try {
    if (!url) {
      const msg = 'Unexpected error. Invalid request URL.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    const formData = new FormData()
    formData.append('file', options.file)

    const { data } = await $axios.post<ApiResponse>(url, formData, {
      onUploadProgress: (event) => {
        if (!event.total) return
        options.onProgress?.(Math.round((event.loaded / event.total) * 100))
      },
    })

    if (!data.success) {
      toast.error(data.message)
      options.onError?.(data.message)
      return null
    }

    const media: AssessmentMedia | null = data.data?.media ?? null

    // A `201` without a body is a broken contract, not a success: returning would leave the block
    // pointing at nothing while the author saw an upload complete.
    if (!media?.path) {
      const msg = 'Unexpected error. The upload response carried no file.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    options.onSuccess?.(media)
    return media
  } catch (err: unknown) {
    const msg = getAxiosErrorMessage(err)
    toast.error(msg)
    options.onError?.(msg)
    return null
  } finally {
    options.onFinish?.()
  }
}
